import { useState, useEffect } from 'react'
import type { Order } from '../../../types'
import { CRYPTO_ASSETS, NETWORKS } from '../../../lib/constants'
import { config } from '../../../config'
import { formatMYR, formatCrypto } from '../../../lib/utils'
import { orderApi } from '../../../lib/api'
import { layout, ui, cx, text, spinner } from '../../../lib/ui-primitives'
import { ExternalLinkIcon } from '../../icons'
import type { useKiosk } from '../../../hooks/useKiosk'

interface ProcessingScreenProps {
  kiosk: ReturnType<typeof useKiosk>
}

export function ProcessingScreen({ kiosk }: ProcessingScreenProps) {
  const { selectedCrypto, selectedNetwork, currentOrder, userDetails, orderId, resetFlow } = kiosk
  const [polledOrder, setPolledOrder] = useState<Order | null>(null)

  // Poll for status updates via API
  useEffect(() => {
    let alive = true

    const poll = async () => {
      if (!orderId) return

      const result = await orderApi.lookup(orderId)
      if (!alive) return

      if (result.success && result.data?.order) {
        const apiOrder = result.data.order
        // Convert API response to Order format
        if (apiOrder.status !== 'pending') {
          setPolledOrder({
            id: apiOrder.id,
            crypto: apiOrder.crypto,
            network: apiOrder.network,
            amountMYR: apiOrder.amountMYR,
            amountCrypto: apiOrder.amountCrypto,
            networkFee: apiOrder.networkFee,
            rate: apiOrder.rate,
            customer: {
              name: apiOrder.customerName,
              contactType: 'telegram',
              contact: '',
              walletAddress: apiOrder.walletAddress,
            },
            paymentRef: '',
            hasProofImage: false,
            status: apiOrder.status,
            txHash: apiOrder.txHash,
            createdAt: new Date(apiOrder.createdAt).getTime(),
            updatedAt: new Date(apiOrder.updatedAt).getTime(),
          })
        }
      }
    }

    poll()
    const interval = setInterval(poll, 5000)
    return () => {
      alive = false
      clearInterval(interval)
    }
  }, [orderId])

  if (!selectedCrypto || !selectedNetwork) return null

  const asset = CRYPTO_ASSETS[selectedCrypto]

  // Show result screen if order status changed
  if (polledOrder && polledOrder.status !== 'pending') {
    const isApproved = polledOrder.status === 'approved' || polledOrder.status === 'completed'

    return (
      <div className={cx(layout.screenContent, 'items-center justify-center')}>
        <div className="text-center w-full max-w-sm">
          {/* Status Icon */}
          {isApproved ? (
            <>
              <div
                className="size-20 rounded-full flex items-center justify-center mb-6 mx-auto"
                style={{ backgroundColor: `${asset.color}33` }}
              >
                <span className="text-4xl" aria-hidden="true">✓</span>
              </div>
              <h2 className={cx(text.h2, 'mb-2')}>Payment Confirmed!</h2>
              <p className={cx('mb-8', text.secondary)}>
                {polledOrder.status === 'completed'
                  ? 'Your crypto has been sent'
                  : 'Processing your crypto transfer'}
              </p>
            </>
          ) : (
            <>
              <div className="size-20 rounded-full bg-red-500/20 border border-red-500/20 flex items-center justify-center mb-6 mx-auto">
                <span className="text-4xl" aria-hidden="true">✗</span>
              </div>
              <h2 className={cx(text.h2, 'mb-2')}>Order Declined</h2>
              <p className={cx('mb-8', text.secondary)}>Please contact support for assistance</p>
            </>
          )}

          {/* Order Details Card */}
          <div className={cx(ui.card, 'p-4 sm:p-5 mb-6 text-left')}>
            <div className="text-sm space-y-3">
              <div className="flex justify-between">
                <span className={text.secondary}>Order ID</span>
                <span className={cx('text-white', text.mono)}>{polledOrder.id}</span>
              </div>
              <div className="flex justify-between">
                <span className={text.secondary}>Amount Paid</span>
                <span className={cx('text-white', text.numeric)}>{formatMYR(polledOrder.amountMYR)}</span>
              </div>
              {isApproved && (
                <>
                  <div className="flex justify-between">
                    <span className={text.secondary}>
                      Crypto {polledOrder.status === 'completed' ? 'Sent' : 'Amount'}
                    </span>
                    <span className={cx(text.mono, text.numeric)} style={{ color: asset.color }}>
                      {formatCrypto(polledOrder.amountCrypto, 4)} {polledOrder.crypto}
                    </span>
                  </div>
                  {polledOrder.txHash && (
                    <div className={cx('pt-3', ui.divider)}>
                      <span className={cx('text-xs block mb-1', text.tertiary)}>Transaction Hash</span>
                      <a
                        href={`${NETWORKS[polledOrder.network].explorer}${polledOrder.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cx(
                          'text-indigo-300 text-xs break-all hover:underline inline-flex items-center gap-1',
                          text.mono
                        )}
                      >
                        {polledOrder.txHash.slice(0, 20)}...{polledOrder.txHash.slice(-10)}
                        <ExternalLinkIcon aria-hidden="true" />
                      </a>
                    </div>
                  )}
                  <div className={cx('pt-3', ui.divider)}>
                    <span className={cx('text-xs block mb-1', text.tertiary)}>To Wallet</span>
                    <span className={cx('text-white text-xs break-all', text.mono)}>
                      {polledOrder.customer.walletAddress}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Done Button */}
          <button
            onClick={resetFlow}
            className={cx(ui.btnBase, ui.btnGhost, 'w-full py-4 font-bold text-lg')}
          >
            Done
          </button>

          <p className={cx('text-xs mt-4', text.disabled)}>
            Questions? Contact {config.supportTelegram}
          </p>
        </div>
      </div>
    )
  }

  // Processing state
  return (
    <div
      className={cx(layout.screenContent, 'items-center justify-center')}
      role="status"
      aria-label="Verifying payment"
    >
      <div className="text-center w-full max-w-sm">
        {/* Loading Spinner */}
        <div className="relative mb-8">
          <div className={cx(spinner.large, spinner.base, 'mx-auto')} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl" aria-hidden="true">⏳</span>
          </div>
        </div>

        <h2 className={cx(text.h2, 'mb-2')}>Verifying Payment</h2>
        <p className={cx('mb-8', text.secondary)}>We're checking your payment</p>

        {/* Order Summary Card */}
        <div className={cx(ui.card, 'p-4 sm:p-5 mb-6')}>
          <div className="text-sm space-y-3">
            <div className="flex justify-between">
              <span className={text.secondary}>Order ID</span>
              <span className={cx('text-white', text.mono)}>{orderId}</span>
            </div>
            <div className="flex justify-between">
              <span className={text.secondary}>Amount</span>
              <span className={cx('text-white', text.numeric)}>
                {formatMYR(currentOrder?.amountMYR || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={text.secondary}>Crypto</span>
              <span className={cx(text.mono, text.numeric)} style={{ color: asset.color }}>
                {formatCrypto(currentOrder?.amountCrypto || 0, 4)} {selectedCrypto}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={text.secondary}>Network</span>
              <span className="text-white">{selectedNetwork}</span>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-2 text-sm">
          <p className={text.tertiary}>
            We'll contact you via {userDetails.contactType === 'telegram' ? 'Telegram' : 'email'}
          </p>
          <p className={cx('text-xs', text.disabled)}>
            Need help? Contact {config.supportTelegram}
          </p>
        </div>

        {/* Start New Order Button */}
        <button
          onClick={resetFlow}
          className={cx(ui.btnBase, ui.btnGhost, 'mt-8 py-3 px-6')}
        >
          Start New Order
        </button>
      </div>
    </div>
  )
}
